import { useEffect, useMemo } from 'react';

import { useLocalSearchParams, usePathname } from 'expo-router';
import * as Updates from 'expo-updates';
import { DiagnosticContributorBoundary } from 'features/diagnostics/components/diagnostic-contributor-boundary';
import { useStoreContext } from 'features/store';
import { getBuildMetadata } from 'modules/bk-build-metadata';
import { Platform } from 'react-native';

import { useAuthContext } from 'state/auth';
import { RunningExperiments, useExperiments } from 'state/experiments';
import { selectors, useAppSelector } from 'state/global-state';
import { useLocale } from 'state/intl';
import { useMainMenuContext } from 'state/menu/main-menu';
import { useServiceModeLive } from 'state/service-mode';
import { useUIContext } from 'state/ui';
import { appVersion, nativeApplicationVersion, nativeBuildVersion } from 'utils/app-version-info';
import { env, getCurrentCommitHash } from 'utils/environment';

import { resolveCartRecoveryMode } from '../../cart/hooks/use-cart-recovery-mode';
import {
  compactDiagnosticVariants,
  getDiagnosticPrincipalKey,
  projectDiagnosticRouteParams,
  projectDiagnosticUser,
  projectLegacyCartEntry,
  projectMinimalCartEntry,
  projectPublicRestaurantAddress,
} from '../context/projectors';
import {
  useDiagnosticContributor,
  useDiagnosticPrincipalUpdater,
  useDiagnosticToolsEnabled,
} from '../context/provider';

export const DiagnosticContextGlobalContributors = () => {
  const enabled = useDiagnosticToolsEnabled();

  return enabled ? (
    <DiagnosticContributorBoundary>
      <EnabledDiagnosticContextGlobalContributors />
    </DiagnosticContributorBoundary>
  ) : null;
};

const EnabledDiagnosticContextGlobalContributors = () => {
  const pathname = usePathname();
  const routeParams = useLocalSearchParams();
  const { locale, region } = useLocale();
  const { getVariantValue } = useExperiments();
  const { user, isAuthenticated, loading: authLoading } = useAuthContext();
  const loyaltyState = useAppSelector(selectors.loyalty.selectLoyaltyUser);
  const { store, availableDayParts, isFetchingStore, isStoreAvailable } = useStoreContext();
  const serviceMode = useServiceModeLive();
  const { activeDayParts, dayParts } = useMainMenuContext();
  const legacyCartEntries = useAppSelector(selectors.ordering.selectCartEntries);
  const minimalCartEntries = useAppSelector(selectors.ordering.selectMinimalCartEntries);
  const rbiOrderId = useAppSelector(selectors.ordering.selectPricedOrderId);
  const { formatCurrencyForLocale } = useUIContext();
  const updatePrincipal = useDiagnosticPrincipalUpdater();

  const minimalCartVariant = getVariantValue(RunningExperiments.EnableMinimalCart)?.value;
  const experimentSelectedImplementation: 'minimal' | 'legacy' =
    minimalCartVariant === 'treatment' ? 'minimal' : 'legacy';
  const effectiveImplementation = resolveCartRecoveryMode({
    isMinimalCartUiEnabled: experimentSelectedImplementation === 'minimal',
    legacyCartEntriesCount: legacyCartEntries.length,
    minimalCartEntriesCount: minimalCartEntries.length,
  });
  const buildMetadata = useMemo(() => getBuildMetadata(), []);
  const currency = String(region).toUpperCase() === 'CA' ? 'CAD' : 'USD';
  const menuImplementation = store ? (store.isOneMenu ? 'one_menu' : 'legacy') : undefined;

  const principalKey = getDiagnosticPrincipalKey({ isAuthenticated, user });
  useEffect(() => updatePrincipal(principalKey), [principalKey, updatePrincipal]);

  const captureGetter = useMemo(
    () => () => ({
      capture: {
        capturedAt: new Date().toISOString(),
        pathname,
        routeParams: projectDiagnosticRouteParams(routeParams),
        platform: Platform.OS,
        osVersion: String(Platform.Version),
        appVersion,
        buildVersion: nativeBuildVersion ?? nativeApplicationVersion ?? undefined,
        otaUpdateId: Updates.updateId ?? undefined,
        otaChannel: Updates.channel ?? undefined,
        commitId: buildMetadata.gitCommitHash ?? getCurrentCommitHash(),
        easBuildId: buildMetadata.easBuildId ?? undefined,
        environment: String(env()),
        region: String(region),
        locale,
        variants: compactDiagnosticVariants({
          menuImplementation,
          effectiveCartImplementation: effectiveImplementation,
          experimentCartImplementation: experimentSelectedImplementation,
          cartRecoveryOverride: effectiveImplementation !== experimentSelectedImplementation,
          offersRewardsImplementation: getVariantValue(
            RunningExperiments.EnableRenderReadyOffersRewards
          )?.value,
        }),
      },
    }),
    [
      buildMetadata.easBuildId,
      buildMetadata.gitCommitHash,
      effectiveImplementation,
      experimentSelectedImplementation,
      getVariantValue,
      locale,
      menuImplementation,
      pathname,
      region,
      routeParams,
    ]
  );
  useDiagnosticContributor('capture-runtime', captureGetter);

  const userGetter = useMemo(
    () => () => ({
      user: projectDiagnosticUser({
        isAuthenticated,
        authLoading,
        user,
        loyaltyProfile: loyaltyState.loyaltyUser,
      }),
    }),
    [authLoading, isAuthenticated, loyaltyState.loyaltyUser, user]
  );
  useDiagnosticContributor('user-live-auth', userGetter);

  const storeGetter = useMemo(
    () => () => ({
      store: store?._id
        ? {
            selectionState: 'selected' as const,
            number: store.number ?? undefined,
            sanityId: store._id ?? undefined,
            name: store.name ?? undefined,
            publicAddress: projectPublicRestaurantAddress(store.physicalAddress),
            posVendor: store.pos?.vendor ?? undefined,
            serviceMode: serviceMode ?? undefined,
            localTime: store.localTime ?? undefined,
            activeDayparts: (availableDayParts ?? activeDayParts).map(daypart => daypart.key),
            configuredDayparts: dayParts.map(daypart => daypart.key),
            serviceModeHours: store.serviceModeHours
              ? JSON.stringify(store.serviceModeHours)
              : undefined,
            daypartHours: store.dayPartHours ? JSON.stringify(store.dayPartHours) : undefined,
            availability: isFetchingStore
              ? 'loading'
              : isStoreAvailable
              ? 'available'
              : 'unavailable',
            isOneMenu: Boolean(store.isOneMenu),
          }
        : { selectionState: 'none' as const },
    }),
    [
      activeDayParts,
      availableDayParts,
      dayParts,
      isFetchingStore,
      isStoreAvailable,
      serviceMode,
      store,
    ]
  );
  useDiagnosticContributor('store-selected', storeGetter);

  const cartGetter = useMemo(
    () => () => {
      const entries =
        effectiveImplementation === 'minimal'
          ? minimalCartEntries.map(entry => projectMinimalCartEntry(entry, formatCurrencyForLocale))
          : legacyCartEntries.map(entry =>
              projectLegacyCartEntry(entry, formatCurrencyForLocale, currency)
            );
      return {
        cart: {
          effectiveImplementation,
          experimentSelectedImplementation,
          recoveryOverride: effectiveImplementation !== experimentSelectedImplementation,
          legacyEntryCount: legacyCartEntries.length,
          minimalEntryCount: minimalCartEntries.length,
          rbiOrderId: rbiOrderId ?? undefined,
          entries,
        },
      };
    },
    [
      currency,
      effectiveImplementation,
      experimentSelectedImplementation,
      formatCurrencyForLocale,
      legacyCartEntries,
      minimalCartEntries,
      rbiOrderId,
    ]
  );
  useDiagnosticContributor('cart-effective', cartGetter);

  return null;
};
